import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Wand2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";

const Field = ({ label, hint, children }) => (
  <div>
    <span className="lab-label">{label} {hint && <span className="normal-case tracking-normal text-slate-600">- {hint}</span>}</span>
    <div className="mt-1">{children}</div>
  </div>
);

const NUMS = Array.from({ length: 10 }, (_, i) => i + 1);

// The lock on this memory: a fragment flashes, then the hive asks for its number (answered on a console dial 1-10).
export const RiddleFields = ({ riddle = {}, name, memory, onChange, idPrefix = "riddle" }) => {
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => onChange({ riddle: { ...riddle, [k]: e.target.value } });
  const setNum = (k) => (e) => onChange({ riddle: { ...riddle, [k]: Number(e.target.value) } });

  const regenerate = async () => {
    if (!memory?.script?.trim()) { toast.error("Write the memory first - the riddle is built from it"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/generate-riddle", { name: name || "object", memory });
      const { source, ...r } = data;
      onChange({ riddle: r });
      toast.success(source === "ai" ? "New riddle written from the memory" : "Generator fallback used - edit the riddle by hand");
    } catch (err) {
      toast.error(errMsg(err, "Could not write a riddle"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="riddle-fields" className="grid gap-3 border border-cyan-300/30 bg-cyan-300/5 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="lab-label !text-cyan-300">The lock · fragment riddle</p>
        <button type="button" data-testid={`${idPrefix}-regenerate`} onClick={regenerate} disabled={busy} className="lab-btn-ghost py-1.5 text-sm">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} {busy ? "Writing..." : "Rewrite riddle from memory"}
        </button>
      </div>
      <Field label="Fragment" hint="flashes for 7s - must contain exactly one countable detail (1-10)">
        <textarea data-testid={`${idPrefix}-fragment`} rows={2} value={riddle.fragment || ""} onChange={set("fragment")} className="lab-input w-full text-sm leading-relaxed" />
      </Field>
      <div className="grid gap-3 sm:grid-cols-[1fr_90px]">
        <Field label="Question"><input data-testid={`${idPrefix}-question`} value={riddle.question || ""} onChange={set("question")} className="lab-input w-full text-sm" /></Field>
        <Field label="Answer">
          <select data-testid={`${idPrefix}-answer`} value={riddle.answer ?? 5} onChange={setNum("answer")} className="lab-input w-full font-mono">{NUMS.map((n) => <option key={n} value={n}>{n}</option>)}</select>
        </Field>
      </div>
      <Field label="Hint" hint="shown after two wrong answers"><input data-testid={`${idPrefix}-hint`} value={riddle.hint || ""} onChange={set("hint")} className="lab-input w-full text-sm" /></Field>
      <div className="grid gap-3 sm:grid-cols-[1fr_90px]">
        <Field label="Recall question" hint="asked later in the run, once this memory has been heard"><input data-testid={`${idPrefix}-recall`} value={riddle.recall || ""} onChange={set("recall")} className="lab-input w-full text-sm" /></Field>
        <Field label="Answer">
          <select data-testid={`${idPrefix}-recall-answer`} value={riddle.recall_answer ?? 5} onChange={setNum("recall_answer")} className="lab-input w-full font-mono">{NUMS.map((n) => <option key={n} value={n}>{n}</option>)}</select>
        </Field>
      </div>
    </div>
  );
};
